import { addDaysUtc, milestoneDate, MILESTONE_ORDER, type BrandSchedule, type MilestoneKey } from './schedule-dates';
import type { WeekPlan } from './week-slots';
import { shiftForNewStart } from './video-dates';

/** Server-owned store kinds; neither accepts direct writes from a browser. */
export const WEEK_ASSIGNMENT_KIND = 'week_assignment';
export const SCRIPT_SCHEDULE_KIND = 'script_schedule';
export const scriptScheduleKey = (creator: string, brand: string) => `${creator}::${brand}`;
export const scriptWeekKey = (creator: string, brand: string, week: number) => `${creator}::${brand}::${week}`;

export interface ScriptWeekRef { creatorEmail: string; brandSlug: string; week: number }

export function parseScriptWeekKey(key: string): ScriptWeekRef | null {
  const [creatorEmail, brandSlug, raw, ...rest] = key.split('::');
  const week = Number(raw);
  return creatorEmail && brandSlug && rest.length === 0 && /^\d+$/.test(raw ?? '') && Number.isSafeInteger(week) && week > 0
    ? { creatorEmail, brandSlug, week } : null;
}

export function isScheduleDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || Number(value.slice(0, 4)) < 100) return false;
  const time = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value;
}

export interface ScheduleMilestones {
  postDate: string;
  intendedWeek: string;
  scriptDueOn: string;
  footageDueOn: string;
  milestones: Record<MilestoneKey, string>;
}

export interface WeekAssignmentSnapshot extends ScheduleMilestones, ScriptWeekRef {
  version: 1;
  /** The first known agreed deadlines; a later reschedule must not erase these. */
  original: ScheduleMilestones;
  assignedAt: number;
  assignedBy: string;
  source: 'confirmed' | 'automatic' | 'existing';
  reschedules: { at: number; by: string; from: ScheduleMilestones; to: ScheduleMilestones }[];
}

export function scheduleMilestones(schedule: BrandSchedule, postDate: string): ScheduleMilestones {
  const milestones = Object.fromEntries(MILESTONE_ORDER.map((key) => [key, milestoneDate(schedule, key, postDate)])) as Record<MilestoneKey, string>;
  const anchor = milestones['script-brand-review-due'];
  return {
    postDate,
    intendedWeek: addDaysUtc(anchor, -new Date(`${anchor}T00:00:00Z`).getUTCDay()),
    scriptDueOn: milestones['scripts-due'],
    footageDueOn: milestones['footage-upload-due'],
    milestones,
  };
}

function validMilestones(raw: unknown): raw is ScheduleMilestones {
  if (!raw || typeof raw !== 'object') return false;
  const value = raw as Partial<ScheduleMilestones>;
  return [value.postDate, value.intendedWeek, value.scriptDueOn, value.footageDueOn].every(isScheduleDate)
    && !!value.milestones && MILESTONE_ORDER.every((key) => isScheduleDate(value.milestones?.[key]));
}

export function readWeekAssignment(raw: unknown): WeekAssignmentSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as WeekAssignmentSnapshot;
  if (value.version !== 1 || !value.creatorEmail || !value.brandSlug || !Number.isSafeInteger(value.week) || value.week < 1
    || !validMilestones(value) || !validMilestones(value.original) || !Number.isFinite(value.assignedAt)
    || typeof value.assignedBy !== 'string' || !['confirmed', 'automatic', 'existing'].includes(value.source)
    || !Array.isArray(value.reschedules) || !value.reschedules.every((r) => Number.isFinite(r.at) && typeof r.by === 'string' && validMilestones(r.from) && validMilestones(r.to))) return null;
  return value;
}

export interface ScriptScheduleAnchor {
  week: number;
  postDate: string;
  confirmedAt: number;
  confirmedBy: string;
}

export interface ScriptSchedulingState {
  creator: string;
  brand: string;
  recommendedDow: number;
  anchor: ScriptScheduleAnchor | null;
  batches: { week: number; postDate: string | null; scriptCount: number; slotCount: number }[];
}

export interface ScriptScheduleInput { creator: string; brand: string; weeks: number[]; startDate: string }
export interface ScriptSchedulePreview extends ScriptScheduleInput {
  token: string;
  rows: (ScheduleMilestones & { week: number })[];
  warnings: string[];
  errors: string[];
  canApply: boolean;
}
export interface ScriptScheduleApplied {
  ok: true;
  appliedWeeks: number[];
  alreadyApplied: boolean;
  state: ScriptSchedulingState;
}

/** A stale/new-week client plan may lack dates; it must not erase server scheduling or custom video dates. */
export function preservePlanDates(next: WeekPlan, prior: WeekPlan | null, postDate?: string): WeekPlan {
  const priorByScript = new Map(prior?.days.filter((day) => day.scriptId).map((day) => [day.scriptId, day.date]) ?? []);
  const days = next.days.map((day, index) => ({
    ...day,
    // A newly written script has a new id, but its slot may already carry an
    // AM's custom date. Matching the old script is preferred; the same slot is
    // the fallback before deriving a default. Blank/invalid dates are absent.
    date: [day.date, day.scriptId ? priorByScript.get(day.scriptId) : undefined, prior?.days[index]?.date]
      .find(isScheduleDate) ?? null,
  }));
  return { ...next, days: postDate ? shiftForNewStart(days, undefined, postDate) : days };
}
